// Google Calendar de la persona (mcp_django apps/google_calendar).
// 🔒 Todo va con el JWT de quien llama: la org y la persona salen del token;
// my-events solo devuelve SU calendario. Si el backend aún no expone un
// endpoint (404) o la org no lo tiene (403/503), la UI se degrada sin romper.

import { useQuery } from '@tanstack/react-query';

import { djangoApiCall } from '@/lib/api';
import { DAY_MS, httpStatus } from './data';

const BASE = '/admin/google-calendar';

export interface GcalConnection {
  user_id: string;
  google_email: string;
  estado: 'active' | 'needs_reconnect' | 'revoked' | string;
}

export type GcalStatus =
  | { kind: 'unavailable' }                       // endpoint/feature no disponible
  | { kind: 'none' }                              // sin conexión propia
  | { kind: 'active'; email: string }
  | { kind: 'reconnect'; email: string };

export interface GcalEvent {
  id: string;
  titulo: string;
  inicio: string;
  fin: string;
  todo_el_dia: boolean;
  ubicacion: string;
  link: string;
  de_la_plataforma: boolean;
  booking_id: number | null;
}

export type GcalEvents =
  | { kind: 'unavailable' }
  | { kind: 'reconnect' }
  | { kind: 'ok'; conectado: boolean; eventos: GcalEvent[] };

const DEGRADE = new Set([403, 404, 405, 501, 503]);

export function useGcalStatus() {
  return useQuery<GcalStatus>({
    queryKey: ['orion-shell', 'gcal', 'status'],
    retry: false,
    queryFn: async () => {
      try {
        const r = await djangoApiCall<{ mi_conexion: GcalConnection | null }>(`${BASE}/status`);
        const c = r?.mi_conexion;
        if (!c || c.estado === 'revoked') return { kind: 'none' };
        if (c.estado === 'needs_reconnect') return { kind: 'reconnect', email: c.google_email };
        return { kind: 'active', email: c.google_email };
      } catch (err) {
        if (DEGRADE.has(httpStatus(err) ?? 0)) return { kind: 'unavailable' };
        throw err;
      }
    },
  });
}

/** Eventos de MI Google Calendar: hoy → +`days` días. */
export function useMyGcalEvents(enabled: boolean, days = 45) {
  return useQuery<GcalEvents>({
    queryKey: ['orion-shell', 'gcal', 'events', days],
    enabled,
    retry: false,
    queryFn: async () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const q = new URLSearchParams({
        desde: from.toISOString(),
        hasta: new Date(from.getTime() + days * DAY_MS).toISOString(),
      });
      try {
        const r = await djangoApiCall<{ conectado: boolean; eventos: GcalEvent[] }>(`${BASE}/my-events?${q}`);
        return { kind: 'ok', conectado: !!r?.conectado, eventos: Array.isArray(r?.eventos) ? r.eventos : [] };
      } catch (err) {
        const s = httpStatus(err) ?? 0;
        if (s === 409) return { kind: 'reconnect' };
        if (DEGRADE.has(s)) return { kind: 'unavailable' };
        throw err;
      }
    },
  });
}

/** URL actual sin query ni hash: a donde Google nos regresa. */
export function currentReturnUrl(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

export async function getConnectUrl(returnTo: string): Promise<string> {
  const q = new URLSearchParams({ return_to: returnTo });
  const r = await djangoApiCall<{ url: string }>(`${BASE}/connect-url?${q}`);
  if (!r?.url) throw new Error('El servidor no devolvió la URL de Google.');
  return r.url;
}

export async function disconnectGcal(): Promise<void> {
  await djangoApiCall(`${BASE}/disconnect`, { method: 'POST', body: JSON.stringify({}) });
}

/** Fecha de Google → epoch ms. `YYYY-MM-DD` (todo el día) = medianoche local. */
export function gcalTime(v: string): number | null {
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-').map(Number);
    return new Date(y, m - 1, d).getTime();
  }
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}
